import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { FaFolderOpen, FaUpload, FaTimes, FaFileAlt } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Documents.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

// Upload endpoints
const UPLOAD_ENDPOINTS = [
  { key: 'identification', label: 'Identification', endpoint: 'uploadIdentity' },
  { key: 'iraCertificate', label: 'IRA Certificate', endpoint: 'uploadIRA' },
  { key: 'kraPin', label: 'KRA PIN', endpoint: 'uploadKRA' },
];

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Documents() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Data ──
  const [subfolders, setSubfolders] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Upload modal ──
  const [uploadModal, setUploadModal] = useState(false);
  const [files, setFiles] = useState({});           // { identification: File, iraCertificate: File, kraPin: File }
  const [uploading, setUploading] = useState(false);

  // ── Files viewer modal ──
  const [viewerModal, setViewerModal] = useState(false);
  const [currentFolder, setCurrentFolder] = useState('');
  const [folderFiles, setFolderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // Fetch subfolders
  const fetchSubfolders = useCallback(async () => {
    if (!agentCode) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/subfolders/${agentCode}`);
      const data = await res.json();
      setSubfolders(data.subfolders || []);
    } catch (e) {
      console.error('Error fetching subfolders:', e);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [agentCode]);

  useEffect(() => {
    if (agentCode) fetchSubfolders();
  }, [agentCode, fetchSubfolders]);

  // ══════════════════════════════════════
  //  UPLOAD KYC
  // ══════════════════════════════════════
  const handleFileChange = useCallback((key, e) => {
    const file = e.target.files[0];
    if (file) setFiles(prev => ({ ...prev, [key]: file }));
  }, []);

  const handleUpload = useCallback(async () => {
    // Validate at least one file is selected
    const selectedFiles = UPLOAD_ENDPOINTS.filter(ep => files[ep.key]);
    if (selectedFiles.length === 0) {
      toast.warn('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    try {
      // Upload all selected files in parallel
      const uploads = selectedFiles.map(ep => {
        const formData = new FormData();
        formData.append('file', files[ep.key]);
        formData.append('agent', agentCode);
        formData.append('proposal', 'KYC');
        return axios.post(`${API}/${ep.endpoint}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: false,
        });
      });

      await Promise.all(uploads);
      toast.success('Documents uploaded successfully');
      setFiles({});
      setUploadModal(false);
      fetchSubfolders(); // refresh folder list
    } catch (e) {
      console.error('Upload error', e);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  }, [files, agentCode, fetchSubfolders]);

  // ══════════════════════════════════════
  //  VIEW FOLDER FILES
  // ══════════════════════════════════════
  const handleFolderClick = useCallback(async (folderName) => {
    setCurrentFolder(folderName);
    setLoadingFiles(true);
    setViewerModal(true);
    try {
      const res = await fetch(`${API}/files/${agentCode}/${folderName}`);
      const data = await res.json();
      setFolderFiles(data.files || []);
    } catch (e) {
      console.error('Error fetching files:', e);
      toast.error('Failed to load files');
      setFolderFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [agentCode]);

  const openFile = useCallback((fileName) => {
    const fileUrl = `${API.replace('/api', '')}/uploads/${agentCode}/${currentFolder}/${fileName}`;
    window.open(fileUrl, '_blank');
  }, [agentCode, currentFolder]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="docs-page">
      <Sidebar />
      <div className="docs-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="docs-top-row">
          <span className="docs-company">Definite Assurance</span>
          <span className="docs-breadcrumb">Dashboard / Documents</span>
        </div>

        <div className="docs-card">
          <h3><FaFolderOpen style={{ marginRight: 8 }} /> Documents</h3>

          <button className="docs-upload-btn" onClick={() => setUploadModal(true)}>
            <FaUpload /> Upload KYC's
          </button>

          <h4 className="docs-section-title">Uploaded Documents</h4>
          <hr className="docs-divider" />

          {/* Loading */}
          {loading && (
            <div className="docs-loading">
              <div className="docs-spinner" />
              <p>Loading documents…</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && subfolders.length === 0 && (
            <div className="docs-empty">
              <FaFolderOpen size={32} color="#ccc" style={{ marginBottom: 10 }} />
              <p>No documents uploaded yet.</p>
            </div>
          )}

          {/* Folder grid */}
          {!loading && subfolders.length > 0 && (
            <div className="docs-folders-grid">
              {subfolders.map((folder, index) => (
                <div
                  key={index}
                  className="docs-folder"
                  onClick={() => handleFolderClick(folder)}
                >
                  <img src="/folder.png" alt={folder} />
                  <span>{folder}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ UPLOAD KYC MODAL ═══════════ */}
      {uploadModal && (
        <div className="docs-modal-overlay" onClick={() => !uploading && setUploadModal(false)}>
          <div className="docs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="docs-modal-header">
              <h4>Upload KYC Documents</h4>
              <button className="docs-modal-close" onClick={() => !uploading && setUploadModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="docs-modal-body">
              {UPLOAD_ENDPOINTS.map(ep => (
                <div key={ep.key} className="docs-upload-group">
                  <label>{ep.label}</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(ep.key, e)}
                    disabled={uploading}
                  />
                </div>
              ))}
            </div>

            <div className="docs-modal-actions">
              <button
                className="docs-cancel-btn"
                onClick={() => setUploadModal(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="docs-submit-btn"
                onClick={handleUpload}
                disabled={uploading}
              >
                <FaUpload style={{ marginRight: 6 }} />
                {uploading ? 'Uploading…' : 'Upload Documents'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ FILE VIEWER MODAL ═══════════ */}
      {viewerModal && (
        <div className="docs-modal-overlay" onClick={() => setViewerModal(false)}>
          <div className="docs-modal wide" onClick={(e) => e.stopPropagation()}>
            <div className="docs-modal-header">
              <h4>{currentFolder} — Files</h4>
              <button className="docs-modal-close" onClick={() => setViewerModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="docs-modal-body">
              {loadingFiles && (
                <div className="docs-loading">
                  <div className="docs-spinner" />
                  <p>Loading files…</p>
                </div>
              )}

              {!loadingFiles && folderFiles.length === 0 && (
                <div className="docs-empty">
                  <p>No files found in this folder.</p>
                </div>
              )}

              {!loadingFiles && folderFiles.length > 0 && (
                <ul className="docs-file-list">
                  {folderFiles.map((file, index) => (
                    <li key={index} onClick={() => openFile(file)}>
                      <FaFileAlt className="file-icon" />
                      <span>{file}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
